import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Plus, X, Trash2, Upload, Loader2 } from "lucide-react";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";

const empty = {
  company_name: "", logo_url: "", industry: "", website: "",
  primary_email: "", primary_phone: "",
  address: { street: "", city: "", state: "", country: "", postal_code: "" },
  main_contacts: [{ name: "", role: "", email: "", phone: "", is_primary: true }],
  iso_standards: [],
  default_certification_body: "",
  certification_status: "In Progress",
  assigned_consultant: "",
  assigned_consultants: [],
  billing_info: { billing_email: "", tax_id: "", payment_terms: "Net 30", currency: "ZAR", billing_address: "" },
  notes: "",
};

const STATUSES = ["Not Certified", "In Progress", "Certified", "Lapsed", "Pending Renewal"];
const STANDARDS = ["ISO 9001", "ISO 14001", "ISO 27001", "ISO 45001", "ISO 13485", "ISO 22301", "ISO 50001"];

export default function ClientModal({ open, initial, onOpenChange, onChanged }) {
  const [form, setForm] = useState(empty);
  const [consultants, setConsultants] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoFileRef = useRef(null);
  const isEdit = !!initial?.slug;

  useEffect(() => {
    if (open) {
      const seedAssigned = initial?.assigned_consultants?.length
        ? initial.assigned_consultants
        : (initial?.assigned_consultant ? [initial.assigned_consultant] : []);
      setForm({ ...empty, ...(initial || {}),
        address: { ...empty.address, ...(initial?.address || {}) },
        billing_info: { ...empty.billing_info, ...(initial?.billing_info || {}) },
        main_contacts: initial?.main_contacts?.length ? initial.main_contacts : empty.main_contacts,
        iso_standards: initial?.iso_standards || [],
        assigned_consultants: seedAssigned,
      });
      Promise.all([
        endpoints.listConsultants().catch(() => []),
        endpoints.listAccounts({ role: "consultant" }).catch(() => []),
      ]).then(([legacy, accts]) => {
        const seen = new Map();
        for (const c of legacy || []) seen.set(c.name, { id: c.id, name: c.name });
        for (const a of accts || []) if (a.full_name) seen.set(a.full_name, { id: a.id, name: a.full_name });
        setConsultants(Array.from(seen.values()));
      });
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (k, v) => setForm((f) => ({ ...f, address: { ...f.address, [k]: v } }));
  const setBilling = (k, v) => setForm((f) => ({ ...f, billing_info: { ...f.billing_info, [k]: v } }));

  const setContact = (i, k, v) => setForm((f) => ({
    ...f, main_contacts: f.main_contacts.map((c, idx) => idx === i ? { ...c, [k]: v } : (k === "is_primary" && v ? { ...c, is_primary: false } : c))
  }));
  const addContact = () => setForm((f) => ({ ...f, main_contacts: [...f.main_contacts, { name: "", role: "", email: "", phone: "", is_primary: false }] }));
  const removeContact = (i) => setForm((f) => ({ ...f, main_contacts: f.main_contacts.filter((_, idx) => idx !== i) }));

  const setStd = (i, k, v) => setForm((f) => ({ ...f, iso_standards: f.iso_standards.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const addStd = () => setForm((f) => ({ ...f, iso_standards: [...f.iso_standards, { standard: "ISO 9001", status: "In Progress", certification_body: form.default_certification_body || "", certification_date: "", expiry_date: "", scope: "" }] }));
  const removeStd = (i) => setForm((f) => ({ ...f, iso_standards: f.iso_standards.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) return toast.error("Company name is required");
    setSaving(true);
    try {
      if (isEdit) {
        await endpoints.updateClient(initial.slug, form);
        toast.success("Client updated");
      } else {
        await endpoints.createClient(form);
        toast.success("Client added");
      }
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save client");
    } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!isEdit) { toast.error("Save the client first, then upload a logo"); return; }
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Image must be ≤ 2 MB"); return; }
    setUploadingLogo(true);
    try {
      const updated = await endpoints.uploadClientLogo(initial.slug, file);
      set("logo_url", updated.logo_url);
      toast.success("Logo uploaded");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploadingLogo(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${initial.company_name}? This does not delete their invoices or appointments.`)) return;
    setSaving(true);
    try { await endpoints.deleteClient(initial.slug); toast.success("Client deleted"); onChanged?.(); }
    catch { toast.error("Could not delete"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="modal-client" className="sm:max-w-[720px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl brand-gradient text-white flex items-center justify-center shadow-md shadow-indigo-500/25">
              <Building2 className="h-4.5 w-4.5" />
            </div>
            <div>
              <DialogTitle>{isEdit ? `Edit ${initial.company_name}` : "New client"}</DialogTitle>
              <DialogDescription>Company profile, contacts, ISO standards and billing.</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 pt-2">
          <section className="space-y-3">
            <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Company</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="c-name">Company name</Label>
                <Input data-testid="client-name-input" id="c-name" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Acme Corp" />
              </div>
              <div>
                <Label htmlFor="c-industry">Industry</Label>
                <Input data-testid="client-industry-input" id="c-industry" value={form.industry} onChange={(e) => set("industry", e.target.value)} />
              </div>
              <div className="col-span-2">
                <Label>Logo</Label>
                <div className="flex items-start gap-3">
                  <div className="h-16 w-16 shrink-0 rounded-2xl bg-white border border-slate-200 dark:border-white/10 shadow-sm flex items-center justify-center overflow-hidden">
                    {form.logo_url ? (
                      <img src={form.logo_url} alt="Logo preview" className="h-full w-full object-contain p-1" />
                    ) : (
                      <Building2 className="h-6 w-6 text-slate-300" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        ref={logoFileRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                        className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadLogo(f); }}
                        data-testid="client-logo-file"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => logoFileRef.current?.click()}
                        disabled={uploadingLogo || !isEdit}
                        data-testid="client-logo-upload"
                      >
                        {uploadingLogo ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                        {form.logo_url ? "Replace" : "Upload logo"}
                      </Button>
                      {form.logo_url && (
                        <Button type="button" variant="ghost" size="sm" className="text-rose-600" onClick={() => set("logo_url", "")}>Remove</Button>
                      )}
                    </div>
                    <Input data-testid="client-logo-input" value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} placeholder="…or paste a URL" />
                    <p className="text-[10px] text-slate-500">PNG, JPEG, WebP, SVG or GIF · ≤ 2 MB. {!isEdit && "Save the client first to enable direct upload."}</p>
                  </div>
                </div>
              </div>
              <div>
                <Label htmlFor="c-website">Website</Label>
                <Input data-testid="client-website-input" id="c-website" value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://..." />
              </div>
              <div>
                <Label htmlFor="c-email">Primary email</Label>
                <Input data-testid="client-email-input" id="c-email" type="email" value={form.primary_email} onChange={(e) => set("primary_email", e.target.value)} />
              </div>
              <div>
                <Label htmlFor="c-phone">Primary phone</Label>
                <Input data-testid="client-phone-input" id="c-phone" value={form.primary_phone} onChange={(e) => set("primary_phone", e.target.value)} />
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Address</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Street</Label><Input data-testid="client-addr-street" value={form.address.street} onChange={(e) => setAddr("street", e.target.value)} /></div>
              <div><Label>City</Label><Input data-testid="client-addr-city" value={form.address.city} onChange={(e) => setAddr("city", e.target.value)} /></div>
              <div><Label>State / Region</Label><Input value={form.address.state} onChange={(e) => setAddr("state", e.target.value)} /></div>
              <div><Label>Country</Label><Input data-testid="client-addr-country" value={form.address.country} onChange={(e) => setAddr("country", e.target.value)} /></div>
              <div><Label>Postal code</Label><Input value={form.address.postal_code} onChange={(e) => setAddr("postal_code", e.target.value)} /></div>
            </div>
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Main contacts</h3>
              <Button type="button" data-testid="client-add-contact" size="sm" variant="ghost" onClick={addContact}><Plus className="h-3.5 w-3.5 mr-1" /> Add contact</Button>
            </div>
            {form.main_contacts.map((c, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-start rounded-xl border border-slate-200 dark:border-white/10 p-2.5 bg-white/60 dark:bg-slate-900/40">
                <Input placeholder="Full name" data-testid={`contact-name-${i}`} value={c.name} onChange={(e) => setContact(i, "name", e.target.value)} />
                <Input placeholder="Role" value={c.role} onChange={(e) => setContact(i, "role", e.target.value)} />
                <Input placeholder="Email" type="email" value={c.email} onChange={(e) => setContact(i, "email", e.target.value)} />
                <Input placeholder="Phone" value={c.phone} onChange={(e) => setContact(i, "phone", e.target.value)} />
                <div className="flex items-center gap-2">
                  <label className="text-[10px] flex items-center gap-1"><input type="checkbox" checked={!!c.is_primary} onChange={(e) => setContact(i, "is_primary", e.target.checked)} /> Primary</label>
                  {form.main_contacts.length > 1 && <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-rose-500" onClick={() => removeContact(i)}><X className="h-3.5 w-3.5" /></Button>}
                </div>
              </div>
            ))}
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">ISO standards & certification</h3>
              <Button type="button" data-testid="client-add-standard" size="sm" variant="ghost" onClick={addStd}><Plus className="h-3.5 w-3.5 mr-1" /> Add standard</Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Overall certification status</Label>
                <Select value={form.certification_status} onValueChange={(v) => set("certification_status", v)}>
                  <SelectTrigger data-testid="client-cert-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Default certification body</Label>
                <Input data-testid="client-cert-body-input" value={form.default_certification_body} onChange={(e) => set("default_certification_body", e.target.value)} placeholder="e.g. BSI, LRQA, SGS" />
              </div>
            </div>
            {form.iso_standards.map((s, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-6 gap-2 rounded-xl border border-slate-200 dark:border-white/10 p-2.5 bg-white/60 dark:bg-slate-900/40">
                <Select value={s.standard} onValueChange={(v) => setStd(i, "standard", v)}>
                  <SelectTrigger data-testid={`std-standard-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{STANDARDS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={s.status} onValueChange={(v) => setStd(i, "status", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                </Select>
                <Input placeholder="Body" value={s.certification_body} onChange={(e) => setStd(i, "certification_body", e.target.value)} />
                <Input type="date" title="Cert date" value={s.certification_date || ""} onChange={(e) => setStd(i, "certification_date", e.target.value)} />
                <Input type="date" title="Expiry" value={s.expiry_date || ""} onChange={(e) => setStd(i, "expiry_date", e.target.value)} />
                <div className="flex items-center gap-1">
                  <Input placeholder="Scope" value={s.scope} onChange={(e) => setStd(i, "scope", e.target.value)} />
                  <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-rose-500 shrink-0" onClick={() => removeStd(i)}><X className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}
          </section>

          <section className="space-y-3">
            <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Assignment & billing</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Allocated consultants</Label>
                <div className="grid grid-cols-2 gap-1.5 rounded-lg border p-2 max-h-40 overflow-y-auto">
                  {consultants.length === 0 && (
                    <div className="col-span-2 px-2 py-2 text-[11px] text-slate-500 italic">No consultants yet — add one from the Accounts page.</div>
                  )}
                  {consultants.map((c) => {
                    const on = (form.assigned_consultants || []).includes(c.name);
                    return (
                      <label key={c.id || c.name} className="flex items-center gap-2 text-xs rounded px-2 py-1 hover:bg-slate-50 cursor-pointer" data-testid={`client-consultant-opt-${c.name}`}>
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => {
                            const cur = form.assigned_consultants || [];
                            const next = on ? cur.filter((n) => n !== c.name) : [...cur, c.name];
                            set("assigned_consultants", next);
                            set("assigned_consultant", next[0] || "");
                          }}
                          className="accent-indigo-600"
                        />
                        <span className="truncate">{c.name}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Pick one or more — the first in the list is the primary consultant on the client card.</p>
              </div>
              <div><Label>Billing email</Label><Input data-testid="client-billing-email-input" type="email" value={form.billing_info.billing_email} onChange={(e) => setBilling("billing_email", e.target.value)} /></div>
              <div><Label>Tax ID</Label><Input value={form.billing_info.tax_id} onChange={(e) => setBilling("tax_id", e.target.value)} /></div>
              <div><Label>Currency</Label><Input value={form.billing_info.currency} onChange={(e) => setBilling("currency", e.target.value)} placeholder="ZAR" /></div>
              <div><Label>Payment terms</Label><Input value={form.billing_info.payment_terms} onChange={(e) => setBilling("payment_terms", e.target.value)} placeholder="Net 30" /></div>
              <div className="col-span-2"><Label>Billing address</Label><Input value={form.billing_info.billing_address} onChange={(e) => setBilling("billing_address", e.target.value)} /></div>
            </div>
          </section>

          <div>
            <Label htmlFor="c-notes">Internal notes</Label>
            <Textarea data-testid="client-notes-input" id="c-notes" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Anything the team should know..." />
          </div>

          <DialogFooter className="flex-wrap gap-2">
            {isEdit && (
              <Button data-testid="client-delete-button" type="button" variant="ghost" className="text-rose-600 mr-auto" onClick={remove} disabled={saving}>
                <Trash2 className="h-4 w-4 mr-1" /> Delete
              </Button>
            )}
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
            <Button data-testid="client-submit-button" type="submit" disabled={saving} className="brand-gradient text-white">
              {saving ? "Saving..." : isEdit ? "Save changes" : "Add client"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
